import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCVEData } from '@/hooks/useCVEData';
import { useCVEPagination } from './hooks/useCVEPagination';

import { CVEYearSelector } from './CVEYearSelector';
import { CVESeverityFilter } from './CVESeverityFilter';
import { CVESearchBar } from './CVESearchBar';
import { CVEStatusIndicator } from './CVEStatusIndicator';
import { CVECard } from './CVECard';
import { CVEPagination } from './CVEPagination';
import { CVELoadingState } from './CVELoadingState';
import { CVEErrorState } from './CVEErrorState';
import { CVENoResults } from './CVENoResults';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Calendar, Star } from 'lucide-react';

export function CVEFeed() {
  const { t } = useLanguage();

  const {
    cveData,
    loadingYears,
    error,
    activeYear,
    selectedSeverities,
    filteredCVEs,
    searchQuery,
    isSearching,
    searchLoading,
    sortOption,
    changeYear,
    toggleSeverity,
    setSearchQuery,
    clearSearch,
    setSortOption
  } = useCVEData();

  const {
    currentPage,
    totalPages,
    setCurrentPage,
    getCurrentItems
  } = useCVEPagination(filteredCVEs);



  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <section className="py-20 bg-secondary/50 dark:bg-secondary/20">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl text-foreground uppercase">
            CVE
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t('cve.subtitle')}
          </p>
          <Separator className="mt-4 mb-8 mx-auto w-24 bg-primary/50" />
        </div>

        {/* Stats Section Removed */}

        {/* Toolbar Section */}
        <div className="glass-card p-6 rounded-xl mb-8 shadow-lg border border-border/50">
          <div className="flex flex-col gap-6">

            {/* Search Bar - Full Width */}
            <div className="w-full">
              <CVESearchBar
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={clearSearch}
                searchLoading={searchLoading}
                filteredResultsCount={filteredCVEs.length}
              />
            </div>

            <Separator className="bg-border/50" />

            {/* Severity Filter - Full Width */}
            <div className="w-full">
              <CVESeverityFilter
                selectedSeverities={selectedSeverities}
                onToggleSeverity={toggleSeverity}
              />
            </div>

            {/* Status indicator */}
            <div className="flex justify-end">
              <CVEStatusIndicator
                isSearching={isSearching}
                loadingYears={loadingYears}
                activeYear={activeYear}
              />
            </div>
          </div>
        </div>

        {loadingYears[activeYear] && !filteredCVEs.length ? (
          <CVELoadingState year={activeYear} isSearching={isSearching} />
        ) : searchLoading ? (
          <CVELoadingState isSearching={true} />
        ) : error ? (
          <CVEErrorState error={error} />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getCurrentItems().map((item, index) => (
                <CVECard key={`${item.cve_id}-${index}`} item={item} index={index} />
              ))}
            </div>

            {/* No results message */}
            {filteredCVEs.length === 0 && (
              <CVENoResults isSearching={isSearching} />
            )}

            {/* Pagination */}
            {filteredCVEs.length > 0 && (
              <CVEPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}
